import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import {
  Shield,
  Lock,
  Copyright,
  QrCode,
  Copy,
  Download,
  Printer,
  BadgeCheck,
  Fingerprint,
  Eye,
  CalendarClock,
  User,
  FileCheck2,
} from 'lucide-react';
import Navbar from '../components/common/Navbar';
import api from '../utils/api';

const CERT_ANIM = `
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}
.cert-fade-in { animation: fadeIn 0.5s ease-out forwards; }
`;

const DEFAULT_DATA = {
  product: { title: '', author: '', cover: '' },
  buyer: { name: '', email: '' },
  payment: { invoice: '', purchaseId: '', method: '', status: '' },
  license: { number: '', issuedAt: '', verifyUrl: '', hash: '', signature: '' },
};

function merge(obj, def) {
  if (!obj) return { ...def };
  const out = { ...def };
  for (const k of Object.keys(def)) {
    out[k] = obj[k] != null && typeof def[k] === 'object' && !Array.isArray(def[k])
      ? merge(obj[k], def[k])
      : obj[k] != null ? obj[k] : def[k];
  }
  return out;
}

function Skeleton() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="animate-pulse space-y-6">
        <div className="flex gap-6">
          <div className="w-32 h-44 bg-gray-200 dark:bg-slate-700 rounded-2xl" />
          <div className="flex-1 space-y-3 pt-2">
            <div className="h-6 w-64 bg-gray-200 dark:bg-slate-700 rounded" />
            <div className="h-4 w-40 bg-gray-200 dark:bg-slate-700 rounded" />
            <div className="h-4 w-48 bg-gray-200 dark:bg-slate-700 rounded" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-28 bg-gray-200 dark:bg-slate-700 rounded-2xl" />
          ))}
        </div>
        <div className="h-40 bg-gray-200 dark:bg-slate-700 rounded-2xl" />
      </div>
    </div>
  );
}

function SecurityCard({ icon: Icon, label, value, onCopy }) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-gray-100 dark:border-slate-700">
      <div className="flex items-center gap-2 mb-3 text-emerald-600">
        <Icon size={18} />
        <span className="text-sm font-medium">{label}</span>
      </div>
      {onCopy ? (
        <div className="flex items-center gap-2">
          <p className="font-mono text-xs text-gray-700 dark:text-gray-300 truncate">{value}</p>
          <button
            onClick={onCopy}
            className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition active:scale-95"
            title="Salin hash"
          >
            <Copy size={14} className="text-gray-400" />
          </button>
        </div>
      ) : (
        <p className="font-mono text-xs text-gray-700 dark:text-gray-300">{value}</p>
      )}
    </div>
  );
}

function Badge({ label }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 shadow-[0_0_12px_rgba(34,197,94,0.3)]">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
      {label}
    </span>
  );
}

export default function CertificatePage() {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const fetchCertificate = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/library/${orderId}/certificate-info`);
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Gagal memuat data sertifikat.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCertificate(); }, [orderId]);

  const d = merge(data, DEFAULT_DATA);

  const copyHash = () => {
    if (!d.license.hash) return;
    navigator.clipboard.writeText(d.license.hash).then(() => toast.success('Hash disalin'));
  };

  const copyLicense = () => {
    if (!d.license.number) return;
    navigator.clipboard.writeText(d.license.number).then(() => toast.success('Nomor lisensi disalin'));
  };

  const downloadPdf = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await api.get(`/api/library/${orderId}/certificate`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'certificate.pdf';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Sertifikat diunduh');
    } catch {
      toast.error('Gagal mengunduh sertifikat');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <Navbar title="Sertifikat" />
        <Skeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <Navbar title="Sertifikat" />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-8 max-w-md mx-auto">
            <Shield size={40} className="mx-auto text-red-500 mb-4" />
            <p className="text-red-700 dark:text-red-400 font-medium mb-4">{error}</p>
            <button
              onClick={fetchCertificate}
              className="px-5 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 active:scale-95 transition"
            >
              Coba Lagi
            </button>
          </div>
        </div>
      </div>
    );
  }

  const watermarkItems = [
    { icon: Eye, label: 'Invisible Watermark' },
    { icon: Eye, label: 'Visible Watermark' },
    { icon: User, label: 'Buyer Metadata' },
    { icon: Fingerprint, label: 'Device Fingerprint' },
    { icon: CalendarClock, label: 'Purchase Timestamp' },
  ];

  return (
    <>
      <style>{CERT_ANIM}</style>
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <Navbar title="Sertifikat" />

        <div className="cert-fade-in max-w-4xl mx-auto px-4 py-10 space-y-8">
          {/* ── Header ── */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100 dark:border-slate-700">
            <div className="flex flex-col sm:flex-row gap-6">
              {d.product.cover && (
                <img
                  src={d.product.cover}
                  alt={d.product.title}
                  className="w-28 h-40 object-cover rounded-xl shadow-md flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-emerald-600 mb-2">
                  <BadgeCheck size={20} />
                  <span className="text-xs font-semibold uppercase tracking-wider">Sertifikat Lisensi Digital</span>
                </div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-1 truncate">
                  {d.product.title || '—'}
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">{d.product.author || '—'}</p>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
                  <span className="flex items-center gap-1.5">
                    <User size={14} /> {d.buyer.name || '—'}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <FileCheck2 size={14} /> Invoice {d.payment.invoice || '—'}
                  </span>
                </div>
                {d.license.issuedAt && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
                    Diterbitkan: {new Date(d.license.issuedAt).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* ── Security Information ── */}
          <section>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Shield size={20} className="text-emerald-600" />
              Informasi Keamanan
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <SecurityCard icon={FileCheck2} label="Document Hash (SHA-256)" value={d.license.hash || '—'} onCopy={d.license.hash ? copyHash : undefined} />
              <SecurityCard icon={Lock} label="Certificate Signature" value={d.license.signature || 'BOOKVAULT DIGITAL SIGNATURE'} />
              <SecurityCard icon={Shield} label="Encryption" value="AES-256" />
              <SecurityCard icon={QrCode} label="Generated" value="Automatically" />
            </div>
          </section>

          {/* ── Watermark Protection ── */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-slate-700">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Lock size={20} className="text-emerald-600" />
              Perlindungan Watermark
            </h2>
            <div className="flex flex-wrap gap-3">
              {watermarkItems.map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-2 bg-gray-50 dark:bg-slate-700/50 rounded-xl px-4 py-2 hover:-translate-y-1 transition-all duration-300"
                >
                  <item.icon size={14} className="text-gray-400 dark:text-gray-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{item.label}</span>
                  <Badge label="Aktif" />
                </div>
              ))}
            </div>
          </div>

          {/* ── Copyright Notice ── */}
          <div className="bg-gray-900 dark:bg-black rounded-2xl p-6 md:p-8 text-white">
            <div className="flex items-start gap-3">
              <Copyright size={22} className="text-emerald-400 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-sm mb-2 text-emerald-400">Pernyataan Hak Cipta</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Dokumen ini merupakan sertifikat kepemilikan digital yang diterbitkan by BookVault.
                  Sertifikat ini bukan bukti kepemilikan hak cipta, melainkan bukti lisensi penggunaan
                  pribadi atas eBook yang dibeli. Seluruh isi eBook tetap menjadi hak eksklusif penulis
                  dan penerbit. Segala bentuk distribusi ulang, penggandaan, atau penyebaran tanpa izin
                  merupakan pelanggaran terhadap Undang-Undang Hak Cipta.
                </p>
              </div>
            </div>
          </div>

          {/* ── QR Code ── */}
          {d.license.verifyUrl && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-slate-700">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <QrCode size={20} className="text-emerald-600" />
                Verifikasi Lisensi
              </h2>
              <div className="flex items-center gap-6 flex-col sm:flex-row">
                <div className="bg-white p-4 rounded-xl shadow-sm transition-transform duration-300 hover:scale-105">
                  <QRCodeSVG value={d.license.verifyUrl} size={140} />
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  <p className="mb-1">Pindai kode QR untuk memverifikasi keaslian lisensi ini.</p>
                  <p className="font-mono text-xs break-all text-gray-400">{d.license.verifyUrl}</p>
                </div>
              </div>
            </div>
          )}

          {/* ── Action Buttons ── */}
          <div className="flex flex-wrap gap-3 pb-8">
            <button
              onClick={downloadPdf}
              disabled={downloading}
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-xl text-sm font-semibold shadow-sm hover:bg-emerald-700 active:scale-95 transition-all duration-150 disabled:opacity-50"
            >
              <Download size={16} />
              {downloading ? 'Mengunduh…' : 'Download Certificate (PDF)'}
            </button>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-6 py-3 border border-emerald-600 text-emerald-600 rounded-xl text-sm font-semibold hover:bg-emerald-50 dark:hover:bg-emerald-900/20 active:scale-95 transition-all duration-150"
            >
              <Printer size={16} />
              Print Certificate
            </button>
            {d.license.verifyUrl && (
              <a
                href={d.license.verifyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-semibold hover:bg-gray-100 dark:hover:bg-slate-800 active:scale-95 transition-all duration-150"
              >
                <BadgeCheck size={16} />
                Verify License
              </a>
            )}
            <button
              onClick={copyLicense}
              className="inline-flex items-center gap-2 px-6 py-3 text-gray-600 dark:text-gray-400 rounded-xl text-sm font-semibold hover:bg-gray-100 dark:hover:bg-slate-800 active:scale-95 transition-all duration-150"
            >
              <Copy size={16} />
              Copy License Number
            </button>
          </div>

          {/* ── Footer ── */}
          <footer className="text-center pb-10 space-y-1">
            <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">&copy; 2026 BookVault</p>
            <p className="text-xs text-gray-400 dark:text-gray-500">Secure Digital Licensing Platform</p>
            <p className="text-xs text-gray-400 dark:text-gray-500">Powered by BookVault DRM</p>
          </footer>
        </div>
      </div>
    </>
  );
}
